/**
 * Naira inputs with thousand separators (en-NG) for modals and forms.
 * Whole-naira fields use display strings like "200,000"; decimal amounts allow "1,234.56".
 * Parse before API calls.
 */

/**
 * @param {unknown} value
 * @returns {string} Grouped whole naira for controlled inputs, or '' if invalid
 */
export function formatWholeNairaFromNumber(value) {
  if (value == null || value === '') return '';
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n < 0) return '';
  if (n > Number.MAX_SAFE_INTEGER) {
    return Number.MAX_SAFE_INTEGER.toLocaleString('en-NG');
  }
  return n.toLocaleString('en-NG');
}

/**
 * @param {unknown} display
 * @returns {number} Parsed integer naira, or NaN if empty/invalid
 */
export function parseWholeNairaToNumber(display) {
  const raw = String(display ?? '')
    .replace(/,/g, '')
    .replace(/\s/g, '')
    .trim();
  if (raw === '') return NaN;
  const n = Number(raw);
  if (!Number.isFinite(n)) return NaN;
  return Math.trunc(n);
}

/**
 * Controlled input handler: keep digits only, format with grouping.
 * @param {string} incoming event.target.value
 * @returns {string} display value (empty if user cleared the field)
 */
export function formatWholeNairaInputChange(incoming) {
  const digits = String(incoming ?? '').replace(/\D/g, '');
  if (digits === '') return '';
  let n = Number(digits);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n > Number.MAX_SAFE_INTEGER) {
    return Number.MAX_SAFE_INTEGER.toLocaleString('en-NG');
  }
  return n.toLocaleString('en-NG');
}

/**
 * @param {unknown} value
 * @param {number} [maxDecimals=2]
 * @returns {string} Grouped amount with optional fraction (en-NG), or '' if invalid
 */
export function formatNairaDecimalFromNumber(value, maxDecimals = 2) {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return '';
  return num.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: maxDecimals });
}

/**
 * @param {unknown} display
 * @returns {number} Parsed non-negative amount, or NaN if empty/invalid
 */
export function parseNairaDecimalToNumber(display) {
  const raw = String(display ?? '')
    .replace(/,/g, '')
    .replace(/\s/g, '')
    .trim();
  if (raw === '' || raw === '.') return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Controlled input: digits + optional single decimal separator, grouped integer part.
 * @param {string} incoming event.target.value
 * @param {number} [maxDecimals=2]
 * @returns {string} display value
 */
export function formatNairaDecimalInputChange(incoming, maxDecimals = 2) {
  const s0 = String(incoming ?? '').replace(/,/g, '');
  let digitsInt = '';
  let digitsFrac = '';
  let dot = false;
  for (let i = 0; i < s0.length; i++) {
    const ch = s0[i];
    if (ch >= '0' && ch <= '9') {
      if (!dot) {
        digitsInt += ch;
      } else if (digitsFrac.length < maxDecimals) {
        digitsFrac += ch;
      }
    } else if ((ch === '.' || ch === ',') && !dot) {
      dot = true;
    }
  }
  if (digitsInt === '' && digitsFrac === '' && !dot) return '';
  if (digitsInt === '' && digitsFrac === '' && dot) return '0.';
  const intNum = digitsInt === '' ? 0 : Number(digitsInt);
  if (!Number.isFinite(intNum) || intNum < 0 || intNum > Number.MAX_SAFE_INTEGER) {
    return '';
  }
  const intShown = intNum.toLocaleString('en-NG');
  if (dot) {
    if (digitsFrac.length > 0) return `${intShown}.${digitsFrac}`;
    return `${intShown}.`;
  }
  return intShown;
}
